import { useStore } from '@nanostores/react';
import * as Popover from '@radix-ui/react-popover';
import { memo, useEffect, useMemo } from 'react';
import { KeyboardShortcut } from './KeyboardShortcut';
import { PromptDetails } from './PromptDetails';
import { SearchEmpty } from './SearchEmpty';
import { SearchResultList } from './SearchResultList';
import { KeyboardShortcutDropdown } from '~/components/chat/commands/KeyboardShortcutDropdown';
import View from '~/components/chat/commands/View';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { commandStore } from '~/lib/stores/command';
import { ActionCommand } from '~/lib/stores/command/models/ActionCommand';
import { BoltPromptCommand } from '~/lib/stores/command/models/BoltPromptCommand';
import { GroupCommand } from '~/lib/stores/command/models/GroupCommand';
import { UserPromptCommand } from '~/lib/stores/command/models/UserPromptCommand';
import { ViewCommand } from '~/lib/stores/command/models/ViewCommand';
import { type AnyCommand, type CommandContext } from '~/lib/stores/command/types';
import { isPromptCommand } from '~/lib/stores/command/utils';
import { supabaseStore } from '~/lib/stores/supabase';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';

interface CommandPopupProps {
  input: string;
  setInput: (input: string) => void;
  focusPromptInput: () => void;
}

const CommandPopup = memo(({ input, setInput, focusPromptInput }: CommandPopupProps) => {
  const selectedIndex = useStore(commandStore.$selectedCommandIndex);
  const root = useStore(commandStore.$root);
  const promptCategories = useStore(commandStore.$promptCategories);
  const customCategories = useStore(commandStore.$customPromptCategories);
  const isShortcutsDropdownOpen = useStore(commandStore.$isShortcutsDropdownOpen);
  const route = useStore(commandStore.$route);
  const routeStack = useStore(commandStore.$routeStack);
  const loggedIn = useStore(userStore.loggedIn);

  useEffect(() => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.PromptLibraryOpened,
      },
    });
  }, []);

  useEffect(() => {
    if (routeStack.length === 0) {
      commandStore.router.push(root);
    }
  }, [root, routeStack.length]);

  const commands = useMemo(() => {
    if (route instanceof GroupCommand) {
      return route.commands;
    }

    return [];
  }, [route, root]);

  const filteredItems = useMemo(() => {
    const query = input.slice(1);

    return commands.filter((command) => command.name?.toLowerCase().includes(query));
  }, [commands, input]);

  const commandContext = useMemo<CommandContext>(
    () => ({
      resetIndex: () => {
        commandStore.setActiveIndex(0);
      },
      setInput,
      supabaseStore,
      commandStore,
      focusPromptInput,
      command: route,
    }),
    [setInput, route],
  );

  const { groups, items } = useMemo(() => {
    if (!filteredItems.length) {
      return {
        groups: [],
        items: [],
      };
    }

    const groupsMap = new Map<string, { id: string; name: string; commands: AnyCommand[] }>();

    if (input === '/') {
      groupsMap.set('favorites', { id: 'favorites', name: 'Favorites', commands: [] });
      groupsMap.set('uncategorized', { id: 'uncategorized', name: '', commands: [] });
      groupsMap.set('commands', { id: 'commands', name: 'Commands', commands: [] });
      groupsMap.set('prompts', { id: 'prompts', name: 'Prompts', commands: [] });

      promptCategories.forEach((category) => {
        groupsMap.set(category.id, {
          id: category.id,
          name: category.name,
          commands: [],
        });
      });
    } else {
      groupsMap.set('results', { id: 'results', name: 'Results', commands: [] });
    }

    filteredItems.forEach((item) => {
      if (!loggedIn && item instanceof ViewCommand) {
        return;
      }

      if (input !== '/') {
        groupsMap.get('results')?.commands.push(item);

        return;
      }

      if (commandStore.isFavorited(item.id)) {
        groupsMap.get('favorites')?.commands.push(item);
      }

      let categoryId = 'uncategorized';

      if (item instanceof ViewCommand) {
        categoryId = 'commands';
      } else if (item instanceof ActionCommand) {
        categoryId = 'commands';
      } else if (item instanceof GroupCommand) {
        categoryId = item.category?.id ?? 'commands';
      } else if (item instanceof UserPromptCommand) {
        categoryId = item.category?.id ?? 'prompts';
      } else if (item instanceof BoltPromptCommand) {
        categoryId = item.category?.id ?? 'prompts';
      }

      const category = groupsMap.get(categoryId);

      if (category) {
        category.commands.push(item);
      } else {
        groupsMap.get('uncategorized')?.commands.push(item);
      }
    });

    let index = 0;

    const groups = Array.from(groupsMap.values())
      .filter((group) => group.commands.length > 0)
      .map((group) => ({
        ...group,
        commands: group.commands.map((command) => ({
          index: index++,
          command,
        })),
      }));

    const items = Array.from(groupsMap.values())
      .flatMap((group) => group.commands)
      .map((command, index) => ({
        index,
        command,
      }));

    return {
      groups,
      items,
    };
  }, [filteredItems, customCategories, promptCategories, input]);

  useEffect(() => {
    if (selectedIndex > items.length - 1 && items.length > 0) {
      commandStore.setActiveIndex(0);
    }
  }, [items, selectedIndex]);

  const highlightedCommand = useMemo(() => {
    return items[selectedIndex]?.command;
  }, [items, selectedIndex]);

  if (!route) {
    return null;
  }

  const shortcuts = [
    {
      label: 'Exit command mode',
      keys: ['Escape'],
      hidden: true,
      disabled: isShortcutsDropdownOpen,
      action: () => {
        commandStore.setActiveIndex(0);
        commandStore.router.pop();

        if (routeStack.length === 1) {
          setInput('');
        }
      },
    },
    {
      label: 'Go to next group',
      keys: ['Meta', 'ArrowDown'],
      hidden: true,
      disabled: commands.length === 0 || isShortcutsDropdownOpen,
      action: () => commandStore.nextGroup(items, groups),
    },
    {
      label: 'Go to previous group',
      keys: ['Meta', 'ArrowUp'],
      hidden: true,
      disabled: commands.length === 0 || isShortcutsDropdownOpen,
      action: () => commandStore.previousGroup(items, groups),
    },
    {
      label: 'Navigate to previous command',
      keys: ['ArrowUp'],
      hidden: true,
      disabled: commands.length === 0 || isShortcutsDropdownOpen,
      action: () => commandStore.previousCommand(items),
    },
    {
      label: 'Navigate to previous command',
      keys: ['Shift', 'Tab'],
      hidden: true,
      disabled: commands.length === 0 || isShortcutsDropdownOpen,
      action: () => commandStore.previousCommand(items),
    },
    {
      label: 'Navigate to next command',
      keys: ['ArrowDown'],
      hidden: true,
      disabled: commands.length === 0 || isShortcutsDropdownOpen,
      action: () => commandStore.nextCommand(items),
    },
    {
      label: 'Navigate to next command',
      keys: ['Tab'],
      hidden: true,
      disabled: commands.length === 0 || isShortcutsDropdownOpen,
      action: () => commandStore.nextCommand(items),
    },
  ];

  const primaryShortcut = highlightedCommand?.shortcuts?.[0];

  return (
    <Popover.Root open={isPromptCommand(highlightedCommand)}>
      <Popover.Anchor>
        <div
          onMouseDown={(event) => event.stopPropagation()}
          className="rounded-lg shadow-lg border border-bolt-elements-borderColor bg-bolt-elements-prompt-commandPopup-background backdrop-blur"
        >
          {route instanceof ViewCommand && <route.view {...commandContext} command={route} />}

          {route instanceof GroupCommand && (
            <View.Container>
              <View.Content>
                {!items.length && <SearchEmpty />}

                {!!items.length && (
                  <SearchResultList groups={groups} selectedIndex={selectedIndex} commandContext={commandContext} />
                )}
              </View.Content>

              {highlightedCommand && (
                <View.Footer>
                  {route instanceof GroupCommand && route.id !== 'root' && <View.FooterTitle title={route.name} />}

                  <KeyboardShortcut
                    key={primaryShortcut.label}
                    label={primaryShortcut.label}
                    keys={primaryShortcut.keys}
                    disabled={isShortcutsDropdownOpen}
                    action={() => primaryShortcut.action(commandContext)}
                  />

                  {loggedIn && (
                    <KeyboardShortcutDropdown
                      shortcuts={highlightedCommand.shortcuts.slice(1)}
                      commandContext={commandContext}
                    >
                      <KeyboardShortcut
                        keys={['Meta', 'K']}
                        action={() => commandStore.$isShortcutsDropdownOpen.set(!isShortcutsDropdownOpen)}
                        label="Options"
                      />
                    </KeyboardShortcutDropdown>
                  )}
                </View.Footer>
              )}
            </View.Container>
          )}
        </div>
      </Popover.Anchor>

      <Popover.Portal>
        <Popover.Content
          side="right"
          sideOffset={8}
          align="start"
          className="outline-none z-command-popup"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {isPromptCommand(highlightedCommand) && <PromptDetails command={highlightedCommand} />}
        </Popover.Content>
      </Popover.Portal>

      {shortcuts.map((shortcut) => (
        <KeyboardShortcut
          hidden
          label={shortcut.label}
          keys={shortcut.keys}
          disabled={shortcut.disabled}
          action={shortcut.action}
        />
      ))}
    </Popover.Root>
  );
});

CommandPopup.displayName = 'CommandPopup';

export { CommandPopup };
